/**
 * "Supplementary Study Materials" row in a module: extra files the mentor
 * shares besides the reading material. Download only, never viewed in the
 * app. A cohort module's are the batch's (kind "template"); a 1:1 student's
 * are on their own card (kind "topic") and never reach another student.
 *
 * Mentors upload files, a folder or a zip (unpacked here), rename, delete
 * with undo, and download. Students pick one file, several, a folder or
 * everything and download it; several files arrive as one zip.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import FolderZipOutlinedIcon from "@mui/icons-material/FolderZipOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import { ConfirmDialog, TextPromptDialog } from "../ContentBank/dialogs";
import { formatBytes, formatDate, plural } from "../ContentBank/format";
import {
  deleteMaterials,
  errorMessage,
  getDownloadUrls,
  listMaterials,
  renameFile,
  renameFolder,
  restoreMaterials,
} from "./studyMaterialsApi";
import { buildTree, idsUnder, isJunk, isZip, normalizePath, relativePath, searchFiles } from "./tree";
import { itemsFromDrop, uploadAll } from "./uploader";
import { downloadAsZip, downloadUrl, extractZip } from "./zipTools";

const FONT = "Be Vietnam Pro";
const ROW_BG = "#E5F1F6";
const BLUE = "#0172A6";

const safeName = (name) => name.replace(/[\\/:*?"<>|]+/g, " ").trim() || "study-materials";

export default function StudyMaterialsPanel({ moduleId, moduleName = "", canEdit = false, initialCount = 0, kind = "template", label = "Supplementary Study Materials" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // { files, total_bytes, can_edit }
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [count, setCount] = useState(initialCount);
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [query, setQuery] = useState("");
  const [uploadMenu, setUploadMenu] = useState(null);
  const [targetFolder, setTargetFolder] = useState(""); // where picked files go
  const [uploads, setUploads] = useState(null); // { items: {path: state}, running, summary }
  const [download, setDownload] = useState(null); // { label, progress }
  const [rename, setRename] = useState(null); // { kind: "file"|"folder", id?, path }
  const [replaceAsk, setReplaceAsk] = useState(null); // { items, clashes }
  const [toast, setToast] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const filesInput = useRef(null);
  const folderInput = useRef(null);
  const zipInput = useRef(null);

  const editable = canEdit && (data ? data.can_edit : true);
  const files = useMemo(() => data?.files || [], [data]);
  const tree = useMemo(() => buildTree(files), [files]);
  const matches = useMemo(() => searchFiles(files, query), [files, query]);
  const byId = useMemo(() => new Map(files.map((f) => [f.id, f])), [files]);
  const selectedSize = useMemo(
    () => [...selected].reduce((n, id) => n + (byId.get(id)?.size_bytes || 0), 0), [selected, byId]);

  const notify = (message, undo) => setToast({ message, undo, key: Date.now() });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const result = await listMaterials(moduleId, kind);
      setData(result);
      setCount(result.files.length);
      setSelected((prev) => new Set([...prev].filter((id) => result.files.some((f) => f.id === id))));
    } catch (error) {
      setLoadError(errorMessage(error, "Couldn't load the study materials"));
    } finally {
      setLoading(false);
    }
  }, [moduleId, kind]);

  useEffect(() => { if (open && !data) load(); }, [open, data, load]);
  useEffect(() => { setCount(initialCount); }, [initialCount]);

  // ─── Selection ────────────────────────────────────────────────────────────

  const toggleIds = (ids, on) => setSelected((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (on ? next.add(id) : next.delete(id)));
    return next;
  });

  const toggleFolder = (path) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(path)) next.delete(path); else next.add(path);
    return next;
  });

  // ─── Download ─────────────────────────────────────────────────────────────

  const runDownload = async (ids, zipName, stripPrefix = "") => {
    if (!ids.length) return;
    try {
      if (ids.length === 1) {
        const [file] = await getDownloadUrls(moduleId, ids, kind);
        if (file) downloadUrl(file.url);
        return;
      }
      setDownload({ label: zipName, progress: 0 });
      const saved = await downloadAsZip(
        async () => (await getDownloadUrls(moduleId, ids, kind)).map((f) => ({
          ...f, path: stripPrefix && f.path.startsWith(stripPrefix) ? f.path.slice(stripPrefix.length) : f.path,
        })),
        zipName,
        (progress) => setDownload({ label: zipName, progress }),
      );
      if (saved) notify(`Downloaded ${zipName}`);
    } catch (error) {
      notify(errorMessage(error, "Download failed"));
    } finally {
      setDownload(null);
    }
  };

  const downloadAll = () => runDownload(files.map((f) => f.id), `${safeName(moduleName)} - study materials.zip`);
  const downloadSelected = () => runDownload([...selected], `${safeName(moduleName)} - selected files.zip`);
  const downloadFolder = (node) => {
    const parent = node.path.includes("/") ? node.path.slice(0, node.path.lastIndexOf("/") + 1) : "";
    runDownload(idsUnder(node), `${safeName(node.name)}.zip`, parent);
  };

  // ─── Upload ───────────────────────────────────────────────────────────────

  const pick = (input, folder = "") => {
    setUploadMenu(null);
    setTargetFolder(folder);
    input.current.value = "";
    input.current.click();
  };

  const queueUpload = (rawItems, folder = targetFolder) => {
    const prefix = folder ? `${folder}/` : "";
    const seen = new Set();
    const items = rawItems
      .map((item) => ({ ...item, path: normalizePath(prefix + item.path) }))
      .filter((item) => item.path && !isJunk(item.path) && !seen.has(item.path) && seen.add(item.path));
    if (!items.length) return;
    const existing = new Set(files.map((f) => f.path));
    const clashes = items.filter((item) => existing.has(item.path));
    if (clashes.length) setReplaceAsk({ items, clashes });
    else runUpload(items);
  };

  const runUpload = async (items) => {
    setReplaceAsk(null);
    const states = Object.fromEntries(items.map((i) => [i.path, { status: "queued", progress: 0, size: i.file.size }]));
    setUploads({ items: states, running: true, summary: null, all: items });
    const onUpdate = (path, patch) => setUploads((prev) => prev && ({
      ...prev, items: { ...prev.items, [path]: { ...(prev.items[path] || {}), ...patch } },
    }));
    const result = await uploadAll(moduleId, items, onUpdate, kind);
    setUploads((prev) => prev && ({ ...prev, running: false, summary: result }));
    await load();
    if (result.uploaded) notify(`Uploaded ${plural(result.uploaded, "file")}`);
  };

  const onPicked = (event) => {
    const picked = [...event.target.files].map((file) => ({ path: relativePath(file), file }));
    queueUpload(picked);
  };

  const onZipPicked = async (event) => {
    const zipFiles = [...event.target.files].filter(isZip);
    if (!zipFiles.length) return;
    const all = [];
    try {
      for (const zip of zipFiles) {
        setDownload({ label: `Unpacking ${zip.name}`, progress: 0 });
        const entries = await extractZip(zip, (progress) => setDownload({ label: `Unpacking ${zip.name}`, progress }));
        // Keep a zip of one folder as that folder; otherwise use the zip's name.
        const tops = new Set(entries.map((e) => e.path.split("/")[0]));
        const single = tops.size === 1 && entries.every((e) => e.path.includes("/"));
        const folder = zip.name.replace(/\.zip$/i, "");
        entries.forEach((e) => all.push({ path: single ? e.path : `${folder}/${e.path}`, file: e.file }));
      }
    } catch (error) {
      notify(`Couldn't unpack the zip: ${errorMessage(error, "it may be damaged")}`);
      return;
    } finally {
      setDownload(null);
    }
    queueUpload(all);
  };

  const onDrop = async (event) => {
    event.preventDefault();
    setDragOver(false);
    if (!editable || uploads?.running) return;
    queueUpload(await itemsFromDrop(event.dataTransfer), "");
  };

  // ─── Rename / delete ──────────────────────────────────────────────────────

  const submitRename = async (value) => {
    try {
      if (rename.kind === "file") {
        const folder = rename.path.includes("/") ? rename.path.slice(0, rename.path.lastIndexOf("/") + 1) : "";
        await renameFile(rename.id, folder + value);
      } else {
        const parent = rename.path.includes("/") ? rename.path.slice(0, rename.path.lastIndexOf("/") + 1) : "";
        await renameFolder(moduleId, rename.path, parent + value, kind);
      }
      setRename(null);
      await load();
    } catch (error) {
      notify(errorMessage(error, "Couldn't rename"));
    }
  };

  const remove = async (ids, label) => {
    try {
      await deleteMaterials(moduleId, ids, kind);
      toggleIds(ids, false);
      await load();
      notify(`Deleted ${label}`, async () => {
        const result = await restoreMaterials(moduleId, ids, kind);
        await load();
        if (result.skipped.length) notify(`${plural(result.skipped.length, "file")} not restored: a newer file has the same name`);
      });
    } catch (error) {
      notify(errorMessage(error, "Couldn't delete"));
    }
  };

  // ─── Rendering ────────────────────────────────────────────────────────────

  const headerMeta = data
    ? `${plural(files.length, "file")} · ${formatBytes(data.total_bytes)}`
    : count ? plural(count, "file") : editable ? "Add files" : "";

  const renderFile = (file, depth, showPath = false) => {
    const isSelected = selected.has(file.id);
    return (
      <Box key={file.id} sx={rowSx(depth, isSelected)}>
        <Checkbox size="small" checked={isSelected} onChange={(e) => toggleIds([file.id], e.target.checked)}
          inputProps={{ "aria-label": `Select ${file.name || file.path}` }} />
        <Box sx={{ width: 24 }} />
        <InsertDriveFileOutlinedIcon sx={{ color: "#6b7280", fontSize: 20, mr: 1 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap sx={{ fontSize: 14, fontFamily: FONT, color: "#202124" }} title={file.path}>
            {showPath ? file.path : file.name}
          </Typography>
        </Box>
        <Typography sx={metaSx}>{formatBytes(file.size_bytes)}</Typography>
        <Typography sx={{ ...metaSx, width: 96, display: { xs: "none", sm: "block" } }}>{formatDate(file.uploaded_at)}</Typography>
        <Box sx={{ display: "flex", width: editable ? 108 : 36, justifyContent: "flex-end" }}>
          <Tooltip title="Download">
            <IconButton size="small" onClick={() => runDownload([file.id])} aria-label={`Download ${file.path}`}>
              <DownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {editable && (
            <>
              <Tooltip title="Rename">
                <IconButton size="small" onClick={() => setRename({ kind: "file", id: file.id, path: file.path })}>
                  <DriveFileRenameOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => remove([file.id], file.path.split("/").pop())}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const renderFolder = (node, depth) => {
    const ids = idsUnder(node);
    const chosen = ids.filter((id) => selected.has(id)).length;
    const isOpen = expanded.has(node.path);
    return (
      <React.Fragment key={`d:${node.path}`}>
        <Box sx={rowSx(depth, false)}>
          <Checkbox size="small" checked={chosen > 0 && chosen === ids.length}
            indeterminate={chosen > 0 && chosen < ids.length}
            onChange={(e) => toggleIds(ids, e.target.checked)}
            inputProps={{ "aria-label": `Select folder ${node.name}` }} />
          <IconButton size="small" onClick={() => toggleFolder(node.path)} sx={{ width: 24, height: 24 }}
            aria-label={isOpen ? `Collapse ${node.name}` : `Expand ${node.name}`}>
            {isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          {isOpen
            ? <FolderOpenOutlinedIcon sx={{ color: "#d97706", fontSize: 20, mr: 1 }} />
            : <FolderOutlinedIcon sx={{ color: "#d97706", fontSize: 20, mr: 1 }} />}
          <Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => toggleFolder(node.path)}>
            <Typography noWrap sx={{ fontSize: 14, fontFamily: FONT, fontWeight: 500, color: "#202124" }}>{node.name}</Typography>
          </Box>
          <Typography sx={metaSx}>{plural(node.fileCount, "file")}</Typography>
          <Typography sx={{ ...metaSx, width: 96, display: { xs: "none", sm: "block" } }}>{formatBytes(node.size)}</Typography>
          <Box sx={{ display: "flex", width: editable ? 108 : 36, justifyContent: "flex-end" }}>
            <Tooltip title="Download folder">
              <IconButton size="small" onClick={() => downloadFolder(node)} disabled={!!download}
                aria-label={`Download folder ${node.name}`}>
                <DownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {editable && (
              <>
                <Tooltip title="Upload into this folder">
                  <IconButton size="small" onClick={() => pick(filesInput, node.path)} disabled={!!uploads?.running}>
                    <FileUploadOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Rename folder">
                  <IconButton size="small" onClick={() => setRename({ kind: "folder", path: node.path })}>
                    <DriveFileRenameOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
        {isOpen && (
          <>
            {node.folders.map((child) => renderFolder(child, depth + 1))}
            {node.files.map((file) => renderFile(file, depth + 1))}
          </>
        )}
      </React.Fragment>
    );
  };

  const uploadStates = uploads ? Object.entries(uploads.items) : [];
  const uploadDone = uploadStates.filter(([, s]) => s.status === "done").length;
  const uploadFailed = uploadStates.filter(([, s]) => s.status === "failed");
  const uploadTotalBytes = uploadStates.reduce((n, [, s]) => n + (s.size || 0), 0) || 1;
  const uploadSentBytes = uploadStates.reduce((n, [, s]) =>
    n + (s.status === "done" ? s.size || 0 : ((s.progress || 0) / 100) * (s.size || 0)), 0);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        backgroundColor={ROW_BG}
        p={1}
        sx={{ cursor: "pointer", "&:hover": { backgroundColor: "#d0e5ed" } }}
        onClick={() => setOpen((v) => !v)}
        role="button"
        aria-expanded={open}
      >
        <AttachFileIcon sx={{ marginRight: 1, color: BLUE, transform: "rotate(45deg)" }} />
        <Typography sx={{ color: "#202124", fontSize: "14px", fontFamily: FONT, flex: 1 }}>
          {label}
        </Typography>
        {headerMeta && (
          <Typography sx={{ color: "#6b7280", fontSize: "13px", fontFamily: FONT, mr: 1 }}>{headerMeta}</Typography>
        )}
        <ExpandMoreIcon sx={{ color: "#5f6368", transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            border: "1px solid #E8E8E8",
            borderTop: 0,
            borderRadius: "0 0 10px 10px",
            p: { xs: 1, sm: 2 },
            backgroundColor: dragOver ? "#f0f7fc" : "#fff",
            outline: dragOver ? `2px dashed ${BLUE}` : "none",
            outlineOffset: -4,
          }}
          onDragOver={editable ? (e) => { e.preventDefault(); setDragOver(true); } : undefined}
          onDragLeave={editable ? () => setDragOver(false) : undefined}
          onDrop={editable ? onDrop : undefined}
        >
          {/* Toolbar */}
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap", mb: 1.5 }}>
            {files.length > 8 && (
              <TextField
                size="small"
                placeholder="Search files"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{ flex: "1 1 200px", maxWidth: 320 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                  endAdornment: query && (
                    <IconButton size="small" onClick={() => setQuery("")} aria-label="Clear search"><CloseIcon fontSize="small" /></IconButton>
                  ),
                }}
              />
            )}
            <Box sx={{ flex: 1 }} />
            {files.length > 0 && (
              <Button size="small" variant="outlined" startIcon={<DownloadOutlinedIcon />} onClick={downloadAll}
                disabled={!!download} sx={buttonSx}>
                Download all ({formatBytes(data?.total_bytes)})
              </Button>
            )}
            {editable && (
              <>
                <Button size="small" variant="contained" startIcon={<FileUploadOutlinedIcon />}
                  onClick={(e) => setUploadMenu(e.currentTarget)} disabled={!!uploads?.running}
                  sx={{ ...buttonSx, backgroundColor: BLUE, boxShadow: "none" }}>
                  Upload
                </Button>
                <Menu anchorEl={uploadMenu} open={!!uploadMenu} onClose={() => setUploadMenu(null)}>
                  <MenuItem onClick={() => pick(filesInput)}>
                    <ListItemIcon><InsertDriveFileOutlinedIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Files" secondary="One or more files" />
                  </MenuItem>
                  <MenuItem onClick={() => pick(folderInput)}>
                    <ListItemIcon><FolderOutlinedIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Folder" secondary="Keeps its subfolders" />
                  </MenuItem>
                  <MenuItem onClick={() => pick(zipInput)}>
                    <ListItemIcon><FolderZipOutlinedIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Zip file" secondary="Unpacked into its folders" />
                  </MenuItem>
                </Menu>
                <input ref={filesInput} type="file" multiple hidden onChange={onPicked} data-testid="files-input" />
                <input ref={folderInput} type="file" multiple hidden onChange={onPicked} webkitdirectory="" directory="" />
                <input ref={zipInput} type="file" accept=".zip,application/zip" multiple hidden onChange={onZipPicked}
                  data-testid="zip-input" />
              </>
            )}
          </Box>

          {/* Busy: zip download or unpack */}
          {download && (
            <Box sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: 13, fontFamily: FONT, color: "#374151", mb: 0.5 }}>
                {download.label.startsWith("Unpacking") ? download.label : `Preparing ${download.label}`} · {download.progress}%
              </Typography>
              <LinearProgress variant="determinate" value={download.progress} />
            </Box>
          )}

          {/* Upload progress */}
          {uploads && (
            <Box sx={{ mb: 1.5, p: 1.5, border: "1px solid #dbe7ef", borderRadius: 2, backgroundColor: "#f8fbfd" }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                <Typography sx={{ fontSize: 13, fontFamily: FONT, fontWeight: 600, flex: 1 }}>
                  {uploads.running
                    ? `Uploading ${uploadDone} of ${plural(uploadStates.length, "file")}…`
                    : `Uploaded ${uploadDone} of ${plural(uploadStates.length, "file")}`}
                  {uploadFailed.length > 0 && ` · ${uploadFailed.length} failed`}
                </Typography>
                {!uploads.running && uploadFailed.length > 0 && (
                  <Button size="small" sx={buttonSx}
                    onClick={() => runUpload(uploads.all.filter((i) => uploads.items[i.path]?.status === "failed"))}>
                    Retry failed
                  </Button>
                )}
                {!uploads.running && (
                  <IconButton size="small" onClick={() => setUploads(null)} aria-label="Close upload summary">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <LinearProgress variant="determinate" value={Math.round((uploadSentBytes / uploadTotalBytes) * 100)} />
              {(uploadFailed.length > 0 || uploadStates.some(([, s]) => s.status === "skipped")) && (
                <Box sx={{ mt: 1, maxHeight: 140, overflow: "auto" }}>
                  {uploadStates.filter(([, s]) => s.status === "failed" || s.status === "skipped").map(([path, s]) => (
                    <Typography key={path} sx={{ fontSize: 12, fontFamily: FONT, color: s.status === "failed" ? "#b91c1c" : "#6b7280" }}>
                      {path}: {s.error || s.status}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {/* Selection bar */}
          {selected.size > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1, px: 1.5, py: 0.75, borderRadius: 2, backgroundColor: "#eaf4fa" }}>
              <Typography sx={{ fontSize: 13, fontFamily: FONT, flex: 1 }}>
                {selected.size} selected · {formatBytes(selectedSize)}
              </Typography>
              <Button size="small" startIcon={<DownloadOutlinedIcon />} onClick={downloadSelected} disabled={!!download} sx={buttonSx}>
                Download
              </Button>
              {editable && (
                <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} sx={buttonSx}
                  onClick={() => remove([...selected], plural(selected.size, "file"))}>
                  Delete
                </Button>
              )}
              <Button size="small" onClick={() => setSelected(new Set())} sx={buttonSx}>Clear</Button>
            </Box>
          )}

          {/* List */}
          {loading && !data && <LinearProgress sx={{ my: 2 }} />}
          {loadError && (
            <Box sx={{ py: 2, textAlign: "center" }}>
              <Typography sx={{ fontSize: 14, color: "#b91c1c", fontFamily: FONT, mb: 1 }}>{loadError}</Typography>
              <Button size="small" onClick={load} sx={buttonSx}>Try again</Button>
            </Box>
          )}
          {data && files.length === 0 && (
            <Box sx={{ py: 4, textAlign: "center", border: editable ? "1px dashed #c7d7e2" : 0, borderRadius: 2 }}>
              <FolderZipOutlinedIcon sx={{ fontSize: 36, color: "#9ca3af" }} />
              <Typography sx={{ fontSize: 14, fontFamily: FONT, color: "#374151", mt: 1 }}>
                {editable ? "No study materials yet" : "Nothing shared here yet"}
              </Typography>
              {editable && (
                <Typography sx={{ fontSize: 13, fontFamily: FONT, color: "#6b7280", mt: 0.5 }}>
                  Drag files or folders here, or use Upload. Students can download them, not view them in the app.
                </Typography>
              )}
            </Box>
          )}
          {data && files.length > 0 && (
            <Box sx={{ border: "1px solid #EEF0F2", borderRadius: 2, overflow: "hidden" }}>
              {query
                ? (matches.length
                  ? matches.map((f) => renderFile({ ...f, name: f.path.split("/").pop() }, 0, true))
                  : <Typography sx={{ p: 2, fontSize: 14, color: "#6b7280", fontFamily: FONT }}>No files match “{query}”</Typography>)
                : (
                  <>
                    {tree.folders.map((node) => renderFolder(node, 0))}
                    {tree.files.map((file) => renderFile(file, 0))}
                  </>
                )}
            </Box>
          )}
        </Box>
      </Collapse>

      <TextPromptDialog
        open={!!rename}
        title={rename?.kind === "folder" ? "Rename folder" : "Rename file"}
        label="Name"
        initial={rename ? rename.path.split("/").pop() : ""}
        onSubmit={submitRename}
        onClose={() => setRename(null)}
      />
      <ConfirmDialog
        open={!!replaceAsk}
        title="Replace existing files?"
        body={replaceAsk
          ? `${plural(replaceAsk.clashes.length, "file")} already exist${replaceAsk.clashes.length === 1 ? "s" : ""} here `
            + `(${replaceAsk.clashes.slice(0, 3).map((c) => c.path).join(", ")}${replaceAsk.clashes.length > 3 ? ", …" : ""}). `
            + "Uploading replaces them for everyone."
          : ""}
        confirmLabel="Replace"
        onConfirm={() => runUpload(replaceAsk.items)}
        onClose={() => setReplaceAsk(null)}
      />
      <Snackbar
        key={toast?.key}
        open={!!toast}
        autoHideDuration={toast?.undo ? 8000 : 4000}
        onClose={(_, reason) => reason !== "clickaway" && setToast(null)}
        message={toast?.message}
        action={toast?.undo && (
          <Button color="inherit" size="small" onClick={async () => {
            const undo = toast.undo;
            setToast(null);
            try { await undo(); } catch (error) { notify(errorMessage(error, "Couldn't undo")); }
          }}>
            Undo
          </Button>
        )}
      />
    </>
  );
}

const rowSx = (depth, isSelected) => ({
  display: "flex",
  alignItems: "center",
  pl: 0.5 + depth * 3,
  pr: 1,
  minHeight: 44,
  borderBottom: "1px solid #F1F3F5",
  backgroundColor: isSelected ? "#f0f7fc" : "transparent",
  "&:last-of-type": { borderBottom: 0 },
  "&:hover": { backgroundColor: isSelected ? "#e6f1f9" : "#fafbfc" },
});

const metaSx = { fontSize: 12, fontFamily: FONT, color: "#6b7280", width: 72, textAlign: "right", flexShrink: 0 };

const buttonSx = { textTransform: "none", fontFamily: FONT, borderRadius: "8px" };
